import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin,
  Youtube, Send, ArrowRight, Heart, Zap, Shield, Award,
  ChevronRight, ExternalLink
} from 'lucide-react';

const FooterModern: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Produits', path: '/products' },
    { name: 'Services', path: '/services' },
    { name: 'À propos', path: '/about' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' }
  ];

  const products = [
    { name: 'Terra', path: '/products/terra' },
    { name: 'Sunu', path: '/products/sunu' },
    { name: 'Terranga', path: '/products/terranga' },
    { name: 'Comparateur', path: '/products#compare' }
  ];

  const services = [
    { name: 'Proxy Power', path: '/proxy-power' },
    { name: 'Stations', path: '/stations' },
    { name: 'Pay As You Go', path: '/pay-as-you-go' },
    { name: 'Formations', path: '/training' },
    { name: 'Investir', path: '/invest' }
  ];

  const legal = [
    { name: 'Mentions légales', path: '/legal' },
    { name: 'Confidentialité', path: '/privacy' },
    { name: 'CGU', path: '/terms' },
    { name: 'Cookies', path: '/cookies' }
  ];

  const socialLinks = [
    { icon: <Facebook size={20} />, url: 'https://facebook.com/proxyemobility', name: 'Facebook', color: 'hover:text-blue-600' },
    { icon: <Instagram size={20} />, url: 'https://instagram.com/proxyemobility', name: 'Instagram', color: 'hover:text-pink-600' },
    { icon: <Twitter size={20} />, url: 'https://twitter.com/proxyemobility', name: 'Twitter', color: 'hover:text-blue-400' },
    { icon: <Linkedin size={20} />, url: 'https://linkedin.com/company/proxyemobility', name: 'LinkedIn', color: 'hover:text-blue-700' },
    { icon: <Youtube size={20} />, url: 'https://youtube.com/@proxyemobility', name: 'YouTube', color: 'hover:text-red-600' }
  ];

  const features = [
    { icon: <Zap size={20} />, text: 'Mobilité 100% électrique' },
    { icon: <Shield size={20} />, text: 'Garantie constructeur' },
    { icon: <Award size={20} />, text: 'Service après-vente 24/7' }
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative">
        {/* Newsletter Section */}
        <div className="border-b border-white/10">
          <div className="container mx-auto px-4 py-12 sm:py-16">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold mb-3">
                    Restez <span className="text-primary-400">informés</span>
                  </h3>
                  <p className="text-gray-300 text-sm sm:text-base">
                    Inscrivez-vous à notre newsletter pour recevoir nos dernières actualités et offres exclusives
                  </p>
                </div>
                <div>
                  <form className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      placeholder="Votre adresse email"
                      className="flex-1 px-4 sm:px-6 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    />
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-600 rounded-full font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Send size={18} />
                      <span>S'inscrire</span>
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="container mx-auto px-4 py-12 sm:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-6">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <Link to="/" className="inline-block mb-6">
                <img src="/proxylogoblanc.png" alt="Proxy Emobility" className="h-10 sm:h-12" />
              </Link>
              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                Révolutionner la mobilité en Afrique avec des solutions électriques innovantes, accessibles et durables.
              </p>

              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                <a href="tel:+221771234567" className="flex items-center gap-3 text-gray-300 hover:text-primary-400 transition-colors group">
                  <div className="p-2 bg-primary-500/20 rounded-lg group-hover:bg-primary-500/30 transition-colors">
                    <Phone size={16} />
                  </div>
                  <span className="text-sm">+221 77 123 45 67</span>
                </a>
                <a href="mailto:hello@proxyemobility.com" className="flex items-center gap-3 text-gray-300 hover:text-primary-400 transition-colors group">
                  <div className="p-2 bg-primary-500/20 rounded-lg group-hover:bg-primary-500/30 transition-colors">
                    <Mail size={16} />
                  </div>
                  <span className="text-sm">hello@proxyemobility.com</span>
                </a>
                <div className="flex items-start gap-3 text-gray-300">
                  <div className="p-2 bg-primary-500/20 rounded-lg mt-0.5">
                    <MapPin size={16} />
                  </div>
                  <span className="text-sm">Face Service Nationale D'hygiène,<br />Rue YF 158, Dakar, Sénégal</span>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-2">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-400 text-sm">
                    <div className="text-primary-400">
                      {feature.icon}
                    </div>
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-4 text-white">Liens rapides</h4>
              <ul className="space-y-2">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.path}
                      className="flex items-center gap-2 text-gray-300 hover:text-primary-400 transition-all text-sm group"
                    >
                      <ChevronRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span className="group-hover:translate-x-1 transition-transform">{link.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Products */}
            <div>
              <h4 className="text-lg font-bold mb-4 text-white">Produits</h4>
              <ul className="space-y-2">
                {products.map((product, index) => (
                  <li key={index}>
                    <Link
                      to={product.path}
                      className="flex items-center gap-2 text-gray-300 hover:text-primary-400 transition-all text-sm group"
                    >
                      <ChevronRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span className="group-hover:translate-x-1 transition-transform">{product.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-bold mb-4 text-white">Services</h4>
              <ul className="space-y-2">
                {services.map((service, index) => (
                  <li key={index}>
                    <Link
                      to={service.path}
                      className="flex items-center gap-2 text-gray-300 hover:text-primary-400 transition-all text-sm group"
                    >
                      <ChevronRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span className="group-hover:translate-x-1 transition-transform">{service.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-lg font-bold mb-4 text-white">Informations</h4>
              <ul className="space-y-2">
                {legal.map((item, index) => (
                  <li key={index}>
                    <Link
                      to={item.path}
                      className="flex items-center gap-2 text-gray-300 hover:text-primary-400 transition-all text-sm group"
                    >
                      <ChevronRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span className="group-hover:translate-x-1 transition-transform">{item.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Language Selector */}
              <div className="mt-6">
                <h5 className="text-sm font-semibold mb-3 text-gray-400">Langue</h5>
                <select className="w-full px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="wo">Wolof</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="container mx-auto px-4 py-6 sm:py-8">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              {/* Copyright */}
              <div className="text-center lg:text-left">
                <p className="text-gray-400 text-sm">
                  © {currentYear} Proxy Emobility. Tous droits réservés.
                </p>
                <p className="text-gray-500 text-xs mt-1 flex items-center justify-center lg:justify-start gap-1">
                  Fait avec <Heart size={12} className="text-red-500 animate-pulse" /> au Sénégal
                </p>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center justify-center w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full transition-all transform hover:scale-110 ${social.color}`}
                    aria-label={social.name}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>

              {/* Certifications/Badges */}
              <div className="flex items-center gap-4">
                <div className="px-4 py-2 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
                  <p className="text-xs text-gray-400">Certifié ISO</p>
                </div>
                <div className="px-4 py-2 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
                  <p className="text-xs text-gray-400">Made in Africa</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterModern;
